import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface DropTrailerAppointmentWindowDialogData {
  title: string;
  orderLabel: string;
  initialStart?: string | null;
  initialEnd?: string | null;
  fallbackDate?: string | null;
}

export interface DropTrailerAppointmentWindowDialogResult {
  start: string | null;
  end: string | null;
}

@Component({
  selector: 'app-drop-trailer-appointment-window-dialog',
  template: `
    <div class="w-[520px] max-w-[94vw] overflow-hidden rounded-2xl bg-white text-slate-950 shadow-2xl">
      <div class="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white px-6 py-5">
        <p class="m-0 text-xs font-black uppercase tracking-[0.14em] text-sky-600">Appointment</p>
        <h2 class="m-0 mt-1 text-xl font-black leading-7 text-slate-950">{{ data.title }}</h2>
        <p class="m-0 mt-1 truncate text-sm font-semibold text-slate-500">{{ data.orderLabel }}</p>
      </div>

      <div class="space-y-4 px-6 py-5">
        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner shadow-slate-200/40">
          <label class="block min-w-0">
            <span class="mb-2 block text-xs font-black uppercase tracking-[0.12em] text-slate-500">Window Start</span>
            <input
              type="datetime-local"
              step="60"
              class="box-border h-12 w-full min-w-0 max-w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-black text-slate-950 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              [(ngModel)]="startValue"
            />
          </label>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner shadow-slate-200/40">
          <label class="block min-w-0">
            <span class="mb-2 block text-xs font-black uppercase tracking-[0.12em] text-slate-500">Window End</span>
            <input
              type="datetime-local"
              step="60"
              class="box-border h-12 w-full min-w-0 max-w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-black text-slate-950 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              [(ngModel)]="endValue"
            />
          </label>
        </div>

        <p *ngIf="!isValid" class="m-0 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
          {{ validationMessage }}
        </p>
      </div>

      <div class="flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-6 py-5">
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-600 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
          (click)="clearWindow()"
        >
          Clear Window
        </button>
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 px-4 text-sm font-black text-slate-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
            (click)="onCancel()"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex min-h-10 items-center justify-center rounded-xl bg-sky-600 px-4 text-sm font-black text-white shadow-lg shadow-sky-950/20 transition hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
            [disabled]="!isValid"
            (click)="onSave()"
          >
            Save Window
          </button>
        </div>
      </div>
    </div>
  `,
})
export class DropTrailerAppointmentWindowDialogComponent {
  startValue = '';
  endValue = '';

  constructor(
    private dialogRef: MatDialogRef<DropTrailerAppointmentWindowDialogComponent, DropTrailerAppointmentWindowDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: DropTrailerAppointmentWindowDialogData
  ) {
    this.startValue = this.normalizeDateTime(data.initialStart);
    this.endValue = this.normalizeDateTime(data.initialEnd);
  }

  get isValid(): boolean {
    const hasAny = !!(this.startValue || this.endValue);
    if (!hasAny) return true;
    if (!(this.startValue && this.endValue)) return false;
    return this.endValue >= this.startValue;
  }

  get validationMessage(): string {
    if (!(this.startValue && this.endValue)) {
      return 'Enter both the start and end of the appointment window, or clear it.';
    }

    return 'The end of the appointment window needs to be after the start.';
  }

  clearWindow(): void {
    this.startValue = '';
    this.endValue = '';
  }

  onSave(): void {
    if (!this.isValid) return;

    if (!this.startValue && !this.endValue) {
      this.dialogRef.close({ start: null, end: null });
      return;
    }

    this.dialogRef.close({
      start: this.composeTimestamp(this.startValue),
      end: this.composeTimestamp(this.endValue),
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private composeTimestamp(value: string): string {
    const parsedValue = this.parseDateTime(value);
    return parsedValue ? `${parsedValue.date} ${parsedValue.time}:00` : '';
  }

  private normalizeDateTime(value: any): string {
    const date = this.normalizeDate(value);
    const time = this.normalizeTime(value);
    return date && time ? `${date}T${time}` : '';
  }

  private parseDateTime(value: string): { date: string; time: string } | null {
    const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    return match ? { date: match[1], time: match[2] } : null;
  }

  private normalizeDate(value: any): string {
    if (value === null || value === undefined) return '';
    const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  private normalizeTime(value: any): string {
    if (value === null || value === undefined) return '';
    const match = String(value).trim().match(/(?:^|\s|T)(\d{2}:\d{2})/);
    return match ? match[1] : '';
  }
}
